// persistency-improvement.js - Tier 2 Data & Research
export const content = {
  heroStyle: 'stats',
  tag: 'Performance Metrics',
  stats: [
    { value: '85%', label: 'Top Quartile Persistency' },
    { value: '65%', label: 'Industry Average' },
    { value: '20pt', label: 'Improvement Potential' },
    { value: '3x', label: 'Revenue Impact' }
  ],

  html: `
<p>Persistency—the percentage of your book that remains in force year over year—is the single most important metric for building a sustainable Medicare business. Yet many agents don't track it, don't understand what drives it, and don't have a systematic approach to improving it.</p>

<p>This guide covers what persistency means, how to measure it accurately, and specific strategies that move the needle.</p>

<h2>Understanding Persistency</h2>

<h3>What Persistency Measures</h3>
<p>Persistency is simply retention expressed as a percentage:</p>

<p><strong>Persistency Rate = (Clients at End of Period / Clients at Start of Period) × 100</strong></p>

<p>If you started the year with 200 clients and ended with 170, your persistency is 85%.</p>

<h3>Why Persistency Matters More Than Sales</h3>
<p>Consider two agents:</p>

<ul>
<li><strong>Agent A:</strong> Enrolls 50 new clients/year, 70% persistency</li>
<li><strong>Agent B:</strong> Enrolls 30 new clients/year, 90% persistency</li>
</ul>

<p>After 5 years:</p>
<ul>
<li>Agent A has ~117 clients (churning through 250 enrollments)</li>
<li>Agent B has ~131 clients (with only 150 enrollments)</li>
</ul>

<p>Agent B built a larger book with 40% less new business effort. That's the power of persistency.</p>

<h2>Measuring Persistency Accurately</h2>

<h3>What to Include</h3>
<ul>
<li><strong>Voluntary disenrollments:</strong> Client chose to leave</li>
<li><strong>Plan-initiated terminations:</strong> Non-payment, eligibility issues</li>
<li><strong>Deaths:</strong> Natural attrition (account for separately)</li>
<li><strong>Moves:</strong> Client left service area</li>
</ul>

<h3>What to Track Separately</h3>
<ul>
<li><strong>Controllable losses:</strong> Client chose competitor, dissatisfaction</li>
<li><strong>Uncontrollable losses:</strong> Death, permanent moves</li>
</ul>

<p>Your controllable persistency is what you can actually influence. If overall persistency is 82% but 5% is deaths and moves, your controllable persistency is 87%—a more actionable number.</p>

<h3>Time Periods</h3>
<ul>
<li><strong>Monthly:</strong> Track for early warning signs</li>
<li><strong>Quarterly:</strong> Review for pattern identification</li>
<li><strong>Annual:</strong> Primary benchmark metric</li>
<li><strong>Rolling 12-month:</strong> Smooth out seasonality</li>
</ul>

<h2>Persistency Benchmarks</h2>

<h3>Medicare Advantage</h3>
<ul>
<li><strong>Below average:</strong> Under 78%</li>
<li><strong>Average:</strong> 78-83%</li>
<li><strong>Good:</strong> 84-88%</li>
<li><strong>Excellent:</strong> 89%+</li>
</ul>

<h3>Medicare Supplement</h3>
<ul>
<li><strong>Below average:</strong> Under 82%</li>
<li><strong>Average:</strong> 82-87%</li>
<li><strong>Good:</strong> 88-92%</li>
<li><strong>Excellent:</strong> 93%+</li>
</ul>

<h3>Part D Standalone</h3>
<ul>
<li><strong>Below average:</strong> Under 75%</li>
<li><strong>Average:</strong> 75-80%</li>
<li><strong>Good:</strong> 81-86%</li>
<li><strong>Excellent:</strong> 87%+</li>
</ul>

<p>Medigap typically has higher persistency than MA because fewer plan changes occur and there's less competitive switching.</p>

<h2>Factors That Drive Persistency</h2>

<h3>Enrollment Quality</h3>
<p>The biggest determinant of persistency happens at enrollment:</p>
<ul>
<li><strong>Provider network verification:</strong> Did you confirm their doctors are covered?</li>
<li><strong>Drug coverage check:</strong> Did you verify their medications are on formulary?</li>
<li><strong>Expectation setting:</strong> Did they understand what they were buying?</li>
<li><strong>Need matching:</strong> Was the plan genuinely right for them?</li>
</ul>

<p>Clients enrolled in the wrong plan leave. Clients enrolled correctly stay.</p>

<h3>First-Year Experience</h3>
<p>First-year persistency is always lower than subsequent years:</p>
<ul>
<li>New clients are still evaluating</li>
<li>Any enrollment errors surface quickly</li>
<li>Relationship isn't established</li>
<li>Competitors are still marketing to them</li>
</ul>

<p>Intensive first-year support dramatically improves first-year persistency, which flows through to lifetime value.</p>

<h3>Ongoing Relationship</h3>
<p>After year one, relationship quality drives persistency:</p>
<ul>
<li>How often do you contact clients?</li>
<li>How quickly do you respond to problems?</li>
<li>How proactive are you about changes?</li>
<li>How valued do clients feel?</li>
</ul>

<h3>External Factors</h3>
<p>Some factors are less controllable but still matter:</p>
<ul>
<li><strong>Plan competitiveness:</strong> Is the plan still good value?</li>
<li><strong>Market conditions:</strong> How aggressive are competitors?</li>
<li><strong>Plan changes:</strong> Did networks or benefits change unfavorably?</li>
<li><strong>Premium increases:</strong> How sensitive are clients to cost?</li>
</ul>

<h2>Improvement Strategies</h2>

<h3>Strategy 1: Fix Enrollment Quality</h3>
<p>Review your lost clients—why did they leave?</p>
<ul>
<li>If network issues: improve provider verification at enrollment</li>
<li>If drug issues: implement formulary checking process</li>
<li>If mismatched plans: slow down and assess needs better</li>
<li>If expectation gaps: improve plan explanation</li>
</ul>

<p>The patterns in your losses tell you where enrollment is failing.</p>

<h3>Strategy 2: Upgrade First-Year Support</h3>
<p>Implement a structured first-year program:</p>
<ul>
<li>Welcome call within 48 hours of effective date</li>
<li>ID card confirmation check at 2 weeks</li>
<li>First-use check-in at 30 days</li>
<li>Quarterly touchpoints through year one</li>
<li>Comprehensive annual review before first AEP</li>
</ul>

<p>First-year clients getting this level of attention persist at much higher rates.</p>

<h3>Strategy 3: Proactive Problem Detection</h3>
<p>Don't wait for clients to call with problems:</p>
<ul>
<li>Monitor for network changes affecting your clients</li>
<li>Watch for formulary changes on commonly-used drugs</li>
<li>Track premium changes and reach out proactively</li>
<li>Review claims patterns that suggest problems</li>
</ul>

<p>Finding problems before clients do shows you're looking out for them.</p>

<h3>Strategy 4: Systematic Outreach</h3>
<p>Clients who hear from you regularly persist better:</p>
<ul>
<li>At minimum: birthday, plan anniversary, annual review</li>
<li>Better: quarterly touchpoints</li>
<li>Best: monthly value-add communications</li>
</ul>

<p>The key is consistency—sporadic outreach doesn't build relationship.</p>

<h3>Strategy 5: Annual Review Excellence</h3>
<p>The annual review is your persistency checkpoint:</p>
<ul>
<li>Conduct thorough needs assessment</li>
<li>Review plan changes honestly</li>
<li>Compare alternatives objectively</li>
<li>Reinforce value when staying makes sense</li>
<li>Help them switch when it doesn't</li>
</ul>

<p>Honest annual reviews build trust. Clients who trust you stay.</p>

<h2>Tracking and Accountability</h2>

<h3>What to Monitor</h3>
<ul>
<li><strong>Overall persistency:</strong> Your headline number</li>
<li><strong>Controllable persistency:</strong> What you can influence</li>
<li><strong>First-year vs. subsequent:</strong> Where the problem is</li>
<li><strong>By plan type:</strong> Which products have issues</li>
<li><strong>By carrier:</strong> Plan-specific problems</li>
<li><strong>By reason:</strong> Why are people leaving</li>
</ul>

<h3>Review Cadence</h3>
<ul>
<li><strong>Weekly:</strong> Check for new cancellations/terminations</li>
<li><strong>Monthly:</strong> Review persistency trends</li>
<li><strong>Quarterly:</strong> Analyze patterns and adjust strategies</li>
<li><strong>Annually:</strong> Comprehensive review and goal-setting</li>
</ul>

<h3>Goal Setting</h3>
<p>Set specific, measurable persistency goals:</p>
<ul>
<li>Start from current baseline</li>
<li>Target 2-3 percentage point improvement annually</li>
<li>Break down by segment if patterns differ</li>
<li>Tie to specific action items</li>
</ul>

<h2>The Compound Effect</h2>

<p>Persistency improvement compounds over time:</p>

<ul>
<li><strong>Year 1:</strong> Improve from 80% to 83%</li>
<li><strong>Year 2:</strong> Improve from 83% to 85%</li>
<li><strong>Year 3:</strong> Improve from 85% to 87%</li>
</ul>

<p>After 5 years, an agent who improved persistency 7 points has a book 35-40% larger than if they'd stayed at 80%—without any additional new business.</p>

<p>That's why persistency is the highest-leverage investment you can make in your business.</p>
  `,

  faqs: [
    {
      question: "What is a good persistency rate for Medicare agents?",
      answer: "For Medicare Advantage: 84-88% is good, 89%+ is excellent. For Medicare Supplement: 88-92% is good, 93%+ is excellent. Industry averages are lower (78-83% for MA, 82-87% for Medigap). Top performers typically achieve 5-10 points above average through systematic retention practices."
    },
    {
      question: "How do I calculate persistency rate?",
      answer: "Persistency Rate = (Clients at End of Period / Clients at Start of Period) × 100. Track separately: controllable losses (competitive switching, dissatisfaction) vs. uncontrollable (death, permanent moves). Your controllable persistency is the actionable metric."
    },
    {
      question: "What has the biggest impact on persistency?",
      answer: "Enrollment quality is the biggest factor—clients enrolled in the wrong plan leave, clients enrolled correctly stay. After that: first-year support (new clients need more attention), ongoing relationship (regular contact builds loyalty), and proactive problem detection (finding issues before clients do)."
    },
    {
      question: "How do I improve first-year persistency specifically?",
      answer: "Implement structured first-year support: welcome call within 48 hours, ID card check at 2 weeks, first-use check-in at 30 days, quarterly touchpoints, and thorough annual review before first AEP. First-year clients getting this attention persist at much higher rates."
    },
    {
      question: "Why does persistency matter more than new sales?",
      answer: "An agent enrolling 30/year with 90% persistency builds a larger book than one enrolling 50/year with 70% persistency—with 40% less effort. Persistency improvements compound: a 7-point improvement over 5 years results in a book 35-40% larger with no additional new business."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage Disenrollment Data",
      url: "https://www.cms.gov/Research-Statistics-Data-and-Systems/Statistics-Trends-and-Reports/MCRAdvPartDEnrolData",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Pro IRP tracks your persistency in real-time, broken down by controllable vs. uncontrollable, first-year vs. subsequent, and by plan/carrier. See exactly where you're losing clients and why, so you can target improvements that actually move your numbers.`
};

export default content;
